import React, { FC, useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { IProfilePictureDTO, IUpdateProfilePictureRequestDTO } from 'src/api/dtos';
import UserPicturesRepository from 'src/api/repositories/common/v1/userPictures.repository';
import APP_VERSION from 'src/app.version';
import config from 'src/config';
import { EditPasswordModal, NotificationsModal, UserPreferencesModal } from 'src/features/settings';
import { getRoute } from 'src/routes';
import {
  Select,
  GlobeIcon,
  MoonIcon,
  EditableAvatar,
  ProfilePictureModal,
  InfoFilledIcon,
  Tooltip,
  Switch,
} from 'src/shared/components';
import { LanguageEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useAsyncEffect, useThunkDispatch } from 'src/shared/hooks';
import { useThemeMode, useTranslate } from 'src/shared/providers';
import { formatInitials, waitForElement } from 'src/shared/utils';
import { getCurrentCultureDisplayName } from 'src/store/localizations/localizations.selectors';
import { RootState } from 'src/store/types';
import { updatePreferences } from 'src/store/user/user.actions';
import { getLanguagePreferenceId, isESS } from 'src/store/user/user.selectors';

import './SettingsMenu.styles.scss';

const GEAR_MENU_ID = 'gear-menu-id';

const userPicturesRepository = new UserPicturesRepository();

interface Props {
  onLogout: () => Promise<void>;
}

export const SettingsMenu: FC<Props> = ({ onLogout }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { toggleThemeMode } = useThemeMode();
  const dispatch = useThunkDispatch();
  const location = useLocation();
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(menuEl);
  const [openUserPreferences, setOpenUserPreferences] = useState<boolean>(false);
  const [openEditPassword, setOpenEditPassword] = useState<boolean>(false);
  const [openNotifications, setOpenNotifications] = useState<boolean>(false);
  const { preferencesError } = useSelector((state: RootState) => state.user);
  const { cultures } = useSelector((state: RootState) => state.localizations);
  const { info, settings } = useSelector((state: RootState) => state.user);
  const hasShowEmployeePicturesSetting = settings?.showEmployeePictures;
  const [openProfilePictureModal, setOpenProfilePictureModal] = useState<boolean>(false);
  const [userPicture, setUserPicture] = useState<IProfilePictureDTO | null>(null);
  const [updatingProfilePicture, setUpdatingProfilePicture] = useState<boolean>(false);
  const userInitials = formatInitials(info?.username);
  const [element, setElement] = useState<Element | undefined>();
  const currentLanguageId = useSelector(getLanguagePreferenceId);
  const currentCultureDisplayName = useSelector(getCurrentCultureDisplayName);
  const shouldDisplayUserPreferences = useSelector(isESS);

  const isMenuDisabled = location.pathname.includes(getRoute('settings'));

  // Fetch user profile picture
  useAsyncEffect(
    async () => {
      if (!hasShowEmployeePicturesSetting) {
        return null;
      }

      return await userPicturesRepository.getPicture();
    },
    setUserPicture,
    (error) => {
      if (error.status === 404 || !error.data?.errorMessage) return;
      toast.info(error.data.errorMessage, { type: toast.TYPE.ERROR });
    },
    [hasShowEmployeePicturesSetting],
  );

  const handleDelete = async () => {
    try {
      // Delete picture options
      setUpdatingProfilePicture(true);
      await userPicturesRepository.delete();
      setUserPicture(null);
      toast.info(t('files.yourProfilePictureWasRemovedSuccessfully'), { type: toast.TYPE.SUCCESS });
    } catch {
      toast.info(t('global.oopsAnErrorOccurredWhileProcessingYourRequest'), {
        type: toast.TYPE.ERROR,
      });
    } finally {
      setUpdatingProfilePicture(false);
    }
  };

  const handleUpdate = async (payload: IUpdateProfilePictureRequestDTO) => {
    try {
      // Update picture options
      setUpdatingProfilePicture(true);
      const response = await userPicturesRepository.update({ ...payload });
      setUserPicture(response);

      toast.info(t('files.yourProfilePictureWasUpdatedSuccessfully'), { type: toast.TYPE.SUCCESS });
    } catch {
      toast.info(t('global.oopsAnErrorOccurredWhileProcessingYourRequest'), {
        type: toast.TYPE.ERROR,
      });
    } finally {
      setUpdatingProfilePicture(false);
    }
  };

  const handleUpload = async (formData: FormData) => {
    try {
      // Upsert picture
      setUpdatingProfilePicture(true);
      const response = await userPicturesRepository.upload(formData);
      setUserPicture(response);

      toast.info(t('files.yourProfilePictureWasUpdatedSuccessfully'), { type: toast.TYPE.SUCCESS });
    } catch {
      toast.info(t('global.oopsAnErrorOccurredWhileProcessingYourRequest'), {
        type: toast.TYPE.ERROR,
      });
    } finally {
      setUpdatingProfilePicture(false);
    }
  };

  useEffect(() => {
    const findElement = async () => {
      const pageWrapper = await waitForElement('#page-wrapper');

      if (!pageWrapper) {
        console.warn(
          'The setting menu could not be rendered properly because the Id element `page-wrapper` was not found.',
        );
        return;
      }

      const el = document.createElement('div');
      pageWrapper.appendChild(el);
      setElement(el);

      return () => {
        pageWrapper.removeChild(el);
      };
    };

    findElement();
  }, [location.pathname]);

  const handleLanguageSelect = (event: SelectChangeEvent<LanguageEnums>) => {
    const id = event.target.value as LanguageEnums;
    dispatch(updatePreferences({ languageId: id }));
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuEl(event.currentTarget);
  };

  const handleClose = () => {
    setMenuEl(null);
  };

  if (!element) {
    return null;
  }

  return createPortal(
    <Stack className="setting-gear-menu" flexDirection="row" alignItems="center" spacing={2} gap={2}>
      <Select
        id="language-selector"
        disabled={!!preferencesError}
        value={currentLanguageId}
        placeholder={currentCultureDisplayName}
        onChange={handleLanguageSelect}
        style={{ color: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)' }}
        startAdornment={
          <>
            <InputAdornment position="start">
              <GlobeIcon color={theme.palette.mode === 'light' ? '#000' : theme.palette.text.disabled} />
            </InputAdornment>
          </>
        }
      >
        {cultures.map((culture, index) => (
          <MenuItem key={`${culture.id}-${index}`} value={culture.id}>
            {culture.displayName}
          </MenuItem>
        ))}
      </Select>
      <EditableAvatar
        id="avatar-menu-btn"
        aria-controls={openMenu ? GEAR_MENU_ID : undefined}
        aria-haspopup="true"
        aria-expanded={openMenu ? 'true' : undefined}
        initials={userInitials}
        picture={userPicture}
        onClick={handleClick}
        sx={{ marginTop: '0 !important' }}
      />
      <Menu
        anchorEl={menuEl}
        id={GEAR_MENU_ID}
        open={openMenu}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.12))',
            mt: 1.5,
            minWidth: 250,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {shouldDisplayUserPreferences && (
          <MenuItem
            disabled={isMenuDisabled}
            id="user-preferences-menu-btn"
            selected={openUserPreferences}
            onClick={() => setOpenUserPreferences(true)}
          >
            {t('settings.userPreferences')}
          </MenuItem>
        )}

        {info?.passwordChangeEnabled && (
          <ComponentWithPermissions permissions={PermissionsEnums.ESS_CHANGE_PASSWORD}>
            <MenuItem
              disabled={isMenuDisabled}
              id="password-menu-btn"
              selected={openEditPassword}
              onClick={() => setOpenEditPassword(true)}
            >
              {t('settings.changePassword')}
            </MenuItem>
          </ComponentWithPermissions>
        )}

        <ComponentWithPermissions permissions={PermissionsEnums.SHOW_NOTIFICATION_MENU}>
          <MenuItem
            disabled={isMenuDisabled}
            id="notifications-menu-btn"
            selected={openNotifications}
            onClick={() => setOpenNotifications(true)}
          >
            {t('settings.notifications')}
          </MenuItem>
        </ComponentWithPermissions>

        {hasShowEmployeePicturesSetting && (
          <MenuItem id="profile-picture-btn" onClick={() => setOpenProfilePictureModal(true)}>
            {t('files.uploadProfilePicture')}
          </MenuItem>
        )}

        <MenuItem
          id="presentation-menu-btn"
          onClick={() => toggleThemeMode(theme.palette.mode === 'light' ? 'dark' : 'light')}
        >
          <ListItemIcon>
            <MoonIcon size={20} />
          </ListItemIcon>
          {t('settings.darkMode')}
          <ListItemIcon style={{ minWidth: '20px' }} sx={{ marginLeft: 'auto', alignSelf: 'center' }}>
            <Switch id="theme-toggle-btn" size="small" checked={theme.palette.mode === 'dark'} />
          </ListItemIcon>
        </MenuItem>

        <MenuItem id="logout-menu-btn" onClick={onLogout} sx={{ color: 'error.main' }}>
          {t('global.logout')}
        </MenuItem>

        <Divider />

        <Stack flexDirection="row" alignItems="center" justifyContent="center" textAlign="center">
          <Typography fontSize="0.75rem" variant="caption" color={(theme) => theme.palette.text.secondary}>
            {t('global.version')} {APP_VERSION}
          </Typography>
          {!!config.environment.env?.length && (
            <Tooltip title={config.environment.env}>
              <IconButton size="small" color="info">
                <InfoFilledIcon size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Menu>

      {shouldDisplayUserPreferences && (
        <UserPreferencesModal open={openUserPreferences} onClose={() => setOpenUserPreferences(false)} />
      )}
      <ComponentWithPermissions permissions={PermissionsEnums.ESS_CHANGE_PASSWORD}>
        <EditPasswordModal open={openEditPassword} onClose={() => setOpenEditPassword(false)} />
      </ComponentWithPermissions>
      <ComponentWithPermissions permissions={PermissionsEnums.SHOW_NOTIFICATION_MENU}>
        <NotificationsModal open={openNotifications} onClose={() => setOpenNotifications(false)} />
      </ComponentWithPermissions>
      {hasShowEmployeePicturesSetting && (
        <ProfilePictureModal
          open={openProfilePictureModal}
          onClose={() => setOpenProfilePictureModal(false)}
          initials={userInitials}
          picture={userPicture}
          onUpload={handleUpload}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          loading={updatingProfilePicture}
        />
      )}
    </Stack>,
    element,
  );
};
